import "./popup.css";

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { createPortal } from "react-dom";

type PopupApi = {
  open: (content: ReactNode) => void;
  close: () => void;
};

const PopupContext = createContext<PopupApi | null>(null);

export function PopupProvider({ children }: { children: ReactNode }) {
  const [content, setContent] = useState<ReactNode | null>(null);

  const close = useCallback(() => setContent(null), []);
  const open = useCallback((node: ReactNode) => setContent(node), []);

  useEffect(() => {
    if (!content) return;
    document.body.style.overflow = "hidden";
    const onKeyDown = (e: KeyboardEvent) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [content, close]);

  const api = useMemo(() => ({ open, close }), [open, close]);

  return (
    <PopupContext.Provider value={api}>
      {children}
      {content &&
        createPortal(
          <div className="popup-backdrop" role="presentation" onMouseDown={close}>
            <div
              className="popup-card"
              role="presentation"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <button
                className="popup-close"
                onClick={close}
                aria-label="Close popup"
                type="button"
              >
                ×
              </button>
              {content}
            </div>
          </div>,
          document.body,
        )}
    </PopupContext.Provider>
  );
}

export function usePopup() {
  const ctx = useContext(PopupContext);
  if (!ctx) throw new Error("usePopup must be used within PopupProvider");
  return ctx;
}
